// API Base URL - can be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetch blogs from the API with pagination
 * @param {number} page - Page number to fetch (default: 1)
 * @returns {Promise<Object>} API response with blogs data and pagination info
 */
export const fetchBlogs = async (page = 1) => {
    try {
        const response = await fetch(`${API_BASE_URL}/blogs?page=${page}`);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                blogs: data.data.data || [],
                pagination: {
                    meta: data.data.meta || {},
                    links: data.data.links || {}
                }
            };
        } else {
            throw new Error(data.message || 'Failed to fetch blogs');
        }
    } catch (error) {
        console.error('Error fetching blogs:', error);
        throw error;
    }
};

/**
 * Fetch a single blog by slug from the API
 * @param {string} slug - Blog slug to fetch
 * @returns {Promise<Object>} API response with blog data
 */
export const fetchBlogBySlug = async (slug) => {
    try {
        const response = await fetch(`${API_BASE_URL}/blogs/${slug}`);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Blog post not found');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                blog: data.data
            };
        } else {
            throw new Error(data.message || 'Failed to fetch blog');
        }
    } catch (error) {
        console.error('Error fetching blog:', error);
        throw error;
    }
};

/**
 * Fetch galleries from the API with pagination
 * @param {number} page - Page number to fetch (default: 1)
 * @returns {Promise<Object>} API response with galleries data and pagination info
 */
export const fetchGalleries = async (page = 1) => {
    try {
        const response = await fetch(`${API_BASE_URL}/galleries?page=${page}`);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                galleries: data.data.data || [],
                pagination: {
                    meta: data.data.meta || {},
                    links: data.data.links || {}
                }
            };
        } else {
            throw new Error(data.message || 'Failed to fetch galleries');
        }
    } catch (error) {
        console.error('Error fetching galleries:', error);
        throw error;
    }
};

/**
 * Fetch blog categories from the API
 * @returns {Promise<Object>} API response with categories data
 */
export const fetchCategories = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/blog-categories`);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                categories: Array.isArray(data.data) ? data.data : (data.data.data || [])
            };
        } else {
            throw new Error(data.message || 'Failed to fetch categories');
        }
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

/**
 * Fetch destinations from the API with pagination
 * @param {number} page - Page number to fetch (default: 1)
 * @param {string} search - Search query string (optional)
 * @param {string} country - Country name to filter by (optional)
 * @returns {Promise<Object>} API response with destinations data and pagination info
 */
export const fetchDestinations = async (page = 1, search = '', country = '') => {
    try {
        let url = `${API_BASE_URL}/destinations?page=${page}`;
        if (search && search.trim()) {
            url += `&search=${encodeURIComponent(search.trim())}`;
        }
        if (country && country.trim()) {
            url += `&country=${encodeURIComponent(country.trim())}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                destinations: data.data.data || [],
                pagination: {
                    meta: data.data.meta || {},
                    links: data.data.links || {}
                }
            };
        } else {
            throw new Error(data.message || 'Failed to fetch destinations');
        }
    } catch (error) {
        console.error('Error fetching destinations:', error);
        throw error;
    }
};

/**
 * Fetch tours from the API with pagination
 * @param {number} page - Page number to fetch (default: 1)
 * @param {string} search - Search query string (optional)
 * @param {string} country - Country name to filter by (optional)
 * @returns {Promise<Object>} API response with tours data and pagination info
 */
export const fetchTours = async (page = 1, search = '', country = '') => {
    try {
        let url = `${API_BASE_URL}/tours?page=${page}`;
        if (search && search.trim()) {
            url += `&search=${encodeURIComponent(search.trim())}`;
        }
        if (country && country.trim()) {
            url += `&country=${encodeURIComponent(country.trim())}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                tours: data.data.data || [],
                pagination: {
                    meta: data.data.meta || {},
                    links: data.data.links || {}
                }
            };
        } else {
            throw new Error(data.message || 'Failed to fetch tours');
        }
    } catch (error) {
        console.error('Error fetching tours:', error);
        throw error;
    }
};

/**
 * Fetch a single tour by slug from the API
 * @param {string} slug - Tour slug to fetch
 * @returns {Promise<Object>} API response with tour data
 */
export const fetchTourBySlug = async (slug) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tours/${slug}`);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Tour not found');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                tour: data.data
            };
        } else {
            throw new Error(data.message || 'Failed to fetch tour');
        }
    } catch (error) {
        console.error('Error fetching tour:', error);
        throw error;
    }
};

/**
 * Fetch tours by destination ID from the API
 * @param {number} destinationId - Destination ID to filter tours
 * @param {number} page - Page number to fetch (default: 1)
 * @returns {Promise<Object>} API response with tours data and pagination info
 */
export const fetchToursByDestination = async (destinationId, page = 1) => {
    try {
        const url = `${API_BASE_URL}/tours?destination_id=${destinationId}&page=${page}`;

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle API response structure
        if (data.success && data.data) {
            return {
                success: true,
                tours: data.data.data || [],
                pagination: {
                    meta: data.data.meta || {},
                    links: data.data.links || {}
                }
            };
        } else {
            throw new Error(data.message || 'Failed to fetch tours');
        }
    } catch (error) {
        console.error('Error fetching tours by destination:', error);
        throw error;
    }
};

